import { ENABLE_US_WEB } from '../../config/env';
import { EventProperty } from '../../tools/Analytics';
import { isInternalRoom } from '../../enums/RoomAudience';
import { Properties } from '../../enums/Analytics';
import { RoomSchema } from '../../definitions/schemas';
import { RoomStatus } from '../../enums/RoomStatus';
import { RoomType } from '../../enums/RoomTypes';
import PageKey from '../../pages/PageKey';

/**
 * Builds the room's link within the same origin as the one being used,
 * to avoid triggering analytics tracking if an organization has a vanity URL.
 * The tracking is triggered in those scenarios because the session belongs to
 * the enable.us domain, but if the room's public link is used, it
 * could point to a vanity URL where the admin user may not be logged in.
 */
export const getOriginRoomLink = (uuid: string) =>
  `${window.location.origin}/${PageKey.Room}/${uuid}`;

/** Drops `?query` and `#hash` from a URL string (stable base for section deep links). */
export const stripUrlSearchAndHash = (url: string): string =>
  url.split(/[?#]/)[0] ?? url;

/**
 * Checks if a url is a vanity url
 */
export const isVanityUrl = (link?: string) => {
  // Safely support undefined and empty strings
  if (!link || link === '') {
    return false;
  }
  return !link.includes(ENABLE_US_WEB);
};

/**
 * Extracts a common set of room tracking properties
 */
export const composeRoomTrackingProperties = (
  room: RoomSchema,
  isFirstTimePublished = false
) => {
  const {
    actionPlans,
    bannerUri,
    buttons,
    contents,
    deal,
    expiresAt,
    invitedContactsOnly,
    layout,
    passcode,
    requiresEmail,
    roomCollaborators,
    roomContactsCount,
    status,
    verifyEmail,
    welcomeMessage,
  } = room;

  let properties: EventProperty = {
    [Properties.COLLABORATOR_NUMBER]: roomCollaborators?.length || 0,
    [Properties.CONTACT_NUMBER]: roomContactsCount ?? 0,
    [Properties.CONTENT_NUMBER]: contents?.length,
    [Properties.HAS_BANNER]: !!bannerUri,
    [Properties.HAS_DEAL]: !!deal,
    [Properties.HAS_EMAIL_VERIFICATION]: verifyEmail,
    [Properties.HAS_EXPIRATION]: !!expiresAt,
    [Properties.HAS_EXPLICIT_INVITE]: invitedContactsOnly,
    [Properties.HAS_MAP]: !!actionPlans?.length,
    [Properties.HAS_PASSCODE]: !!passcode,
    [Properties.HAS_REQUIRES_EMAIL]: requiresEmail,
    [Properties.HAS_WELCOME_MESSAGE]: !!welcomeMessage,
    [Properties.LAYOUT]: layout,
    [Properties.SALES_ACTIONS_NUMBER]: buttons?.length,
  };

  if (isFirstTimePublished) {
    properties = {
      ...properties,
      [Properties.CREATED_FROM]:
        status === RoomStatus.DRAFT ? 'draft' : 'directly',
    };
  }

  return properties;
};

/**
 * Extracts a common set of content shared link room tracking properties
 */
export const composeSharedLinkTrackingProperties = (room: RoomSchema) => {
  const {
    company,
    expiresAt,
    invitedContactsOnly,
    notificationSettings,
    passcode,
    requiresEmail,
    roomCollaborators,
    verifyEmail,
  } = room;

  return {
    [Properties.AUTO_NUDGE]: !!notificationSettings?.nudgeContacts,
    [Properties.COLLABORATOR_NUMBER]: roomCollaborators?.length || 0,
    [Properties.EMAIL_NOTIFICATIONS]: !!notificationSettings?.newVisitEmail,
    [Properties.HAS_COMPANY]: !!company?.id,
    [Properties.HAS_EMAIL_VERIFICATION]: verifyEmail,
    [Properties.HAS_EXPIRATION]: !!expiresAt,
    [Properties.HAS_EXPLICIT_INVITE]: invitedContactsOnly,
    [Properties.HAS_PASSCODE]: !!passcode,
    [Properties.HAS_REQUIRES_EMAIL]: requiresEmail,
  };
};

/**
 * Checks if it's necessary to perform a redirection to one of the valid
 * domains for the room, which are the org's vanity URL or the fallback/default
 * URL from the org's vendor. This is only done if the domain from which
 * the room is being accessed is not any of these:
 *  1. The organization's vanity URL
 *  2. The tenant's URL (in case the room is accessed from a Mindtickle's tenant)
 *  3. The default URL from the org's vendor
 *
 * @note This is also used for content links, but the user is redirected to a
 * valid domain but on the content link path
 */
export const isDomainRedirectionRequired = (room: RoomSchema): boolean => {
  const { organization, userLink, uuid } = room;

  const isInternal = isInternalRoom(room);

  const homeUrl = organization?.vendor?.homeUrl;
  const tenantUrl = organization?.vendorTenantUrl;
  const vanityUrl = organization?.vanityUrl;

  const currentDomain = window.location.hostname;
  const homeDomain = homeUrl ? new URL(homeUrl).hostname : '';
  const tenantDomain = tenantUrl ? new URL(tenantUrl).hostname : '';
  const userLinkDomain = userLink ? new URL(userLink).hostname : '';
  const vanityDomain = vanityUrl ? new URL(vanityUrl).hostname : '';

  /**
   * Redirection scenarios if visiting from a non-supported domain:
   *  - External rooms: redirect to room organization's vanity URL
   *  - Internal rooms: redirect to the room's user link, forcing the user
   *                    to be logged in or prompt them to authenticate
   *  - Fallback: to the vendor's home URL
   */
  if (isInternal && userLink && currentDomain !== userLinkDomain) {
    window.location.href = userLink;
    return true;
  }

  const targetUrl = vanityUrl || tenantUrl || homeUrl;
  if (!targetUrl) {
    return false;
  }

  // don't redirect if the current domain is one of the valid ones
  if ([homeDomain, tenantDomain, vanityDomain].includes(currentDomain)) {
    return false;
  }

  const pageKey =
    room.roomType === RoomType.ContentLink ? PageKey.ContentLink : PageKey.Room;

  const newUrl = new URL(`${targetUrl}/${pageKey}/${uuid}`);
  window.location.href = newUrl.href;

  return true;
};
